import { useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import KogModalSideComponent from "@/main/components/modal/ModalSide";
import AreaDeadlineAnswerRequote from "@/main/components/shared/areaDeadlineAnswerRequote/areaDeadlineAnswerRequote";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";

import { IKogModalProps } from "@/main/@types/interfaceIcons";
import { claimApi } from "@/main/api";
import { KeyValueDefault } from "@/main/models/common";
import {
	ClaimItemsToRequoteResponse,
	GetItemsToRequoteResponse,
	QuoteNoSupplierAssignedItemResponse,
} from "@/main/models/response";
import { Controls } from "@/main/services/constants/constantsControl";
import {
	getDefaultCountryOptionDrop,
	getTranslateByKey,
	isEmpty,
	isNullUndefined,
	isNullUndefinedEmpty,
	isShowControlByElementId,
} from "@/main/services/utils";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getPartTypesByDirectAssignCriteria } from "@/main/store/partType";

import SectionRequoteProcurement from "./sectionRequoteProcurement/sectionRequoteProcurement";
import SectionRequoteQuote from "./sectionRequoteQuote/sectionRequoteQuote";
import {
	IItemFormRequoteOrdered,
	INITIAL_FORM_STATE_PROCUREMENT,
	INITIAL_FORM_STATE_REQUOTE,
	IQuoteNoSupplierAssignedItemResponse,
	TypeSectionPart,
} from "./typesModalRequoteClaim";
import {
	agroupPartByNameSupplier,
	getReasonByEnumReasonType,
	getReasonOptionClaim,
	IGetReasonOptionClaimResponse,
	orderByName,
} from "./utilsModalRequoteClaim";
import * as S from "./styles";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import { SendUpdatesSignalRTypeEnum } from "@/main/services/enum/SendUpdatesSignalRTypeEnum";
import { signalRClearReceiveUpdatesByKey } from "@/main/store/signalR";
import { RequoteClaimItemRequest, RequotePartClaimRequest } from "@/main/models/request";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import HubContainer from "@/main/components/shared/hub/HubContainer";
import useKogControlElementId from "@/main/hooks/useKogControlElementId";
import Button from "@/main/components/shared/button/button";

interface IModalRequoteClaimProps extends IKogModalProps {
	guidClaim: string;
	refreshClaim: () => void;
}
const INITIAL_FORM_STATE = {
	sectionQuote: INITIAL_FORM_STATE_REQUOTE,
	sectionProcurement: INITIAL_FORM_STATE_PROCUREMENT,
	reason: null as KeyValueDefault | null,
	reasonDescription: "",
	isRequoteNational: false,
	deadlineAnswer: 0 as number,
};
export type IRequoteClaim = typeof INITIAL_FORM_STATE;
function ModalRequoteClaim({ isOpenModal, guidClaim, closeModal, refreshClaim }: IModalRequoteClaimProps) {
	const SHOW_DEADLINE = useKogControlElementId(Controls.IdRequoteAnswerProcurementTime);
	const SHOW_REQUOTE = useKogControlElementId(Controls.IdRequoteOrdered);
	const SHOW_NATIONAL_REQUOTE = useKogControlElementId(Controls.IdRequoteNationalProcurement);

	const { countriesUser: countryReducer } = useCountryReducer();
	const [getConfirmationModal, ModalConfirmation] = useAsyncModalConfirm();

	//#region hooks
	const dispatch = useDispatch();
	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);

	//#region states
	const [isLoadingParts, setIsLoadingParts] = useState(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [isErrorSignalR, setIsErrorSignalR] = useState<boolean>(false);
	const [isLoadingSignalR, setIsLoadingSignalR] = useState<boolean>(false);
	const [dataParts, setDataParts] = useState<GetItemsToRequoteResponse | null>(null);
	const [, setIsRequotePartCanceled] = useState<boolean>(false);
	const [isLoadingReasonsRequote, setIsLoadingReasonsRequote] = useState<boolean>(false);
	const [reasonsRequoteClaim, setReasonsRequoteClaim] = useState<IGetReasonOptionClaimResponse | null>(null);

	let initialDeadline = dataParts?.paramSupplierDeadlineAnswer || 0;
	const minDeadline = dataParts?.paramMinCustomDeadlineAnswer || 30;
	const maxDeadline = dataParts?.ParamMaxCustomDeadlineAnswer || 300;
	if (initialDeadline < minDeadline) initialDeadline = minDeadline;
	if (initialDeadline > maxDeadline) initialDeadline = maxDeadline;

	INITIAL_FORM_STATE.deadlineAnswer = initialDeadline;
	//#endregion

	const methods = useForm<IRequoteClaim>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});
	const {
		control,
		formState: { isValid },
		getValues,
		setValue,
		watch,
	} = methods;

	const { replace } = useFieldArray({ control, name: "sectionQuote.parts" });
	const { replace: replaceProcurement } = useFieldArray({
		control,
		name: "sectionProcurement.suppliers",
	});

	const controlledFielsQuote = useWatch({
		control,
		name: "sectionQuote.parts",
	});
	const controlledFielsProcurement = useWatch({
		control,
		name: "sectionProcurement.suppliers",
	});

	const watcherReasonType = watch("reason");
	const isReasonOther = convertStringForBoolean(watcherReasonType?.default);
	const showDeadlineAnswerInput = isShowControlByElementId(Controls.IdRequoteAnswerProcurementTime);

	//#endregion

	//#region Core functions
	function getDeadlineAnswer(): number | undefined {
		if (!showDeadlineAnswerInput) return;
		return getValues("deadlineAnswer");
	}

	function validateForm() {
		const dataRoot = getValues();
		const dataQuote = dataRoot.sectionQuote;
		const dataProcurement = dataRoot.sectionProcurement;
		const hasOneSelected =
			dataQuote.parts.some((i) => i.isSelected) ||
			dataProcurement.suppliers.some((s) => s.items.some((i) => i.isSelected));

		if (isNullUndefined(dataRoot.reason)) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.reason").toLowerCase(),
				),
			);
			return false;
		}

		if (isReasonOther && isNullUndefinedEmpty(dataRoot.reasonDescription)) {
			toast.error(getTranslateByKey("information.descriptionRequired", true));
			return false;
		}

		if (!hasOneSelected) {
			const messageError = getTranslateByKey("information.selectOne", true).replace(
				"{0}",
				getTranslateByKey("information.part").toLowerCase(),
			);
			toast.error(messageError);
			return false;
		}
		if (showDeadlineAnswerInput) {
			if (!isValid) {
				const messageError = getTranslateByKey("quote.requoteDeadlineAnswerInvalid", true)
					.replace("{0}", minDeadline.toString())
					.replace("{1}", maxDeadline.toString())
					.replace("{2}", (getDeadlineAnswer() || "").toString());

				toast.error(messageError);
				return false;
			}
		}
		return true;
	}
	interface IGetAllOptionsSelectedResponse {
		quote: IQuoteNoSupplierAssignedItemResponse[];
		procurement: IItemFormRequoteOrdered[];
	}
	function getAllOptionsSelected(data: IRequoteClaim) {
		const allOptionsSelectedQuote: IQuoteNoSupplierAssignedItemResponse[] = [];
		const allOptionsSelectedProcurement: IItemFormRequoteOrdered[] = [];

		data.sectionQuote.parts.forEach((item) => {
			if (item.isSelected) {
				allOptionsSelectedQuote.push(item);
			}
		});

		data.sectionProcurement.suppliers.forEach((supplier) => {
			supplier.items.forEach((item) => {
				if (item.isSelected) {
					allOptionsSelectedProcurement.push(item);
				}
			});
		});

		const allOptionsSelected = {
			quote: allOptionsSelectedQuote,
			procurement: allOptionsSelectedProcurement,
		};

		return allOptionsSelected;
	}

	function getOptionWithReason(allOptionsSelected: IGetAllOptionsSelectedResponse) {
		const result: RequoteClaimItemRequest[] = [];
		const data = getValues();

		allOptionsSelected.quote.forEach((item) => {
			const option: RequoteClaimItemRequest = {
				guidQuoteItem: item.guidQuoteItem,
				guidReason: getReasonByEnumReasonType(
					reasonsRequoteClaim,
					Number(data.reason?.key),
					ReasonTypeEnum.RequoteQuote,
				),
				reasonDescription: data.reasonDescription,
				replacePartType: isShowControlByElementId(Controls.IdSearchProcurementRequotePartType),
				idPartType: isNullUndefined(item.partType) ? null : Number(item.partType.default),
			};
			result.push(option);
		});

		allOptionsSelected.procurement.forEach((item) => {
			const option: RequoteClaimItemRequest = {
				guidProcurementSupplierItem: item.procurementSupplierItemGuid,
				guidReason: getReasonByEnumReasonType(
					reasonsRequoteClaim,
					Number(data.reason?.key),
					ReasonTypeEnum.RequoteProcurement,
				),
				reasonDescription: data.reasonDescription,
				replacePartType: isShowControlByElementId(Controls.IdSearchProcurementRequotePartType),
				idPartType: isNullUndefined(item.partType) ? null : Number(item.partType.default),
			};
			result.push(option);
		});

		return result;
	}

	//#endregion
	//#region async functions
	async function loadPartsRequote() {
		if (isLoadingParts) return;

		setIsLoadingParts(true);
		claimApi
			.getItemsToRequote(guidClaim)
			.then((response) => {
				const {
					data: { result: resultApi },
				} = response;
				const resultSupplier = agroupPartByNameSupplier(resultApi);
				const result = orderByName(resultSupplier);
				setDataParts(result);

				const allInactive = result.unansweredQuoteItems?.every((item) => item.active === false);

				if (allInactive) {
					setIsRequotePartCanceled(true);
				}
				function addItemsToForm(result: ClaimItemsToRequoteResponse) {
					const suppliers = result.suppliers.map((supplier) => {
						const items = supplier.items.map((item) => ({
							...item,
							partType: isEmpty(item.idPartType)
								? null
								: { key: item.idPartType, value: "", default: String(item.idPartType) },
							isSelected: !item.canceledBySupplier,
						}));
						return {
							...supplier,
							items,
						};
					});

					return suppliers;
				}
				function mapDataPartsToFields(
					type: TypeSectionPart,
					dataParts: QuoteNoSupplierAssignedItemResponse[],
				): IQuoteNoSupplierAssignedItemResponse[] {
					const result: IQuoteNoSupplierAssignedItemResponse[] = dataParts.map((item) => ({
						...item,
						sectionPart: type,
						partType: isNullUndefined(item.idPartType)
							? null
							: { key: item.idPartType, value: "", default: String(item.idPartType) },
						isSelected: true,
					}));

					return result;
				}

				const mapperDataPartsAbovePrice = mapDataPartsToFields("abovePrive", result.abovePriceCapQuoteItems);
				const mapperDataPartsUnaswered = mapDataPartsToFields("unanswered", result.unansweredQuoteItems);
				const mapperDataPartsProcurement = addItemsToForm(result);

				const resultFields = [...mapperDataPartsAbovePrice, ...mapperDataPartsUnaswered];
				replace(resultFields);

				replaceProcurement(mapperDataPartsProcurement);
			})
			.finally(() => {
				setIsLoadingParts(false);
			});
	}

	async function saveRequote(data: IRequoteClaim) {
		setIsLoadingSave(true);
		setIsErrorSignalR(false);
		dispatch(signalRClearReceiveUpdatesByKey(SendUpdatesSignalRTypeEnum.RequoteClaim));
		const allOptionsSelected = getAllOptionsSelected(data);
		const itemsRequest = getOptionWithReason(allOptionsSelected);

		const request: RequotePartClaimRequest = {
			requoteItems: itemsRequest,
			isNational: data.isRequoteNational,
			deadlineAnswerRequote: showDeadlineAnswerInput ? data.deadlineAnswer : undefined,
		};

		claimApi
			.requoteItemsClaim(request)
			.then(() => {
				setIsLoadingSignalR(true);
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}

	async function onSubmit(isRequoteNational = false) {
		if (!validateForm()) return;

		const messageRequote = getTranslateByKey("information.confirmRequoteOrdered");
		const isConfirmModal = await getConfirmationModal(messageRequote);
		if (isConfirmModal) {
			setValue("isRequoteNational", isRequoteNational);
			methods.handleSubmit(saveRequote)();
		}
	}

	async function loadReasonDrop() {
		if (isNullUndefined(optionDefaultCountryDrop)) return;
		setIsLoadingReasonsRequote(true);

		const responseReasons = await getReasonOptionClaim(String(optionDefaultCountryDrop.key));

		setReasonsRequoteClaim(responseReasons);
		setIsLoadingReasonsRequote(false);
	}

	function onFinishedRequote() {
		setIsLoadingSignalR(false);
		closeModal();
		refreshClaim();
	}

	function onErrorRequote() {
		setIsLoadingSignalR(false);
		setIsErrorSignalR(true);
	}

	useEffect(() => {
		if (!isOpenModal && isErrorSignalR) {
			dispatch(signalRClearReceiveUpdatesByKey(SendUpdatesSignalRTypeEnum.RequoteClaim));
			setIsErrorSignalR(false);
			setIsLoadingSignalR(false);
		}
	}, [dispatch, isErrorSignalR, isOpenModal]);

	//#endregion

	//#region UseEffects
	useEffect(() => {
		if (isOpenModal) {
			if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
			dispatch(getPartTypesByDirectAssignCriteria({ guidClaim }));
			loadPartsRequote();
		}
	}, [isOpenModal]);

	useEffect(() => {
		loadReasonDrop();
	}, [optionDefaultCountryDrop]);
	//#endregion

	const showButtonRequote = SHOW_REQUOTE && (!isLoadingSignalR || isErrorSignalR);
	const showButtonRequoteNational = SHOW_NATIONAL_REQUOTE && (!isLoadingSignalR || isErrorSignalR);

	return (
		<S.Container>
			<FormProvider {...methods}>
				{!isLoadingSignalR && (
					<>
						<SectionRequoteQuote
							fieldsQuote={controlledFielsQuote}
							isLoadingParts={isLoadingParts}
							maxDeadline={maxDeadline}
							minDeadline={minDeadline}
						/>

						<SectionRequoteProcurement fieldsProcurement={controlledFielsProcurement} isLoadingParts={isLoadingParts} />
					</>
				)}
				<S.AreaFooter>
					{SHOW_DEADLINE && !isLoadingParts && !isLoadingSignalR && (
						<AreaDeadlineAnswerRequote
							min={minDeadline}
							max={maxDeadline}
							control={control}
							customName="deadlineAnswer"
						/>
					)}

					{!isLoadingSignalR && (
						<S.AreaReason>
							<KogAutoCompleteControlled
								id="id-requote-parts-quote-select-reason"
								nameCustom="reason"
								isDefaultValueUnique
								label={getTranslateByKey("information.reason")}
								control={control}
								rulesCustom={{ required: true }}
								setValueCustom={setValue}
								options={reasonsRequoteClaim?.mergedReasons ?? []}
								isLoading={isLoadingReasonsRequote}
								onChangeCustom={(option) => {
									if (option === null) return;

									if (convertStringForBoolean((option as KeyValueDefault)?.default)) {
										setValue("reasonDescription", "");
									}
								}}
							/>
							{isReasonOther && (
								<KogInputTextControlledNew
									required={isReasonOther}
									id="id-requote-quote-input-description"
									label={getTranslateByKey("information.description")}
									name="reasonDescription"
									control={control}
								/>
							)}
						</S.AreaReason>
					)}

					{(isLoadingSignalR || isErrorSignalR) && (
						<HubContainer
							type={SendUpdatesSignalRTypeEnum.RequoteClaim}
							customProps={{
								onFinishedRequote: onFinishedRequote,
								onErrorRequote: onErrorRequote,
							}}
						/>
					)}

					{(!isLoadingSignalR || isErrorSignalR) && (
						<S.AreaButtons>
							<SecondaryButton size="small" id="id-searclaim-btn-back" type="button" onClick={closeModal}>
								{getTranslateByKey("information.back")}
							</SecondaryButton>
							{showButtonRequote && (
								<Button
									size="small"
									id="id-searclaim-btn-save"
									type="button"
									isLoading={isLoadingSave}
									onClick={() => onSubmit(false)}
								>
									{getTranslateByKey("information.requote")}
								</Button>
							)}
							{showButtonRequoteNational && (
								<Button
									size="small"
									id="id-searclaim-btn-requote-national"
									type="button"
									isLoading={isLoadingSave}
									onClick={() => onSubmit(true)}
								>
									{getTranslateByKey("information.requoteNational")}
								</Button>
							)}
						</S.AreaButtons>
					)}
				</S.AreaFooter>
				<ModalConfirmation />
			</FormProvider>
		</S.Container>
	);
}

function WrapperModalRequoteClaim(props: IModalRequoteClaimProps) {
	const titleModal = getTranslateByKey("information.requoteParts");
	return (
		<KogModalSideComponent shouldRenderOpen title={titleModal} open={props.isOpenModal} closeModal={props.closeModal}>
			<ModalRequoteClaim {...props} />
		</KogModalSideComponent>
	);
}
export default WrapperModalRequoteClaim;
